import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  DollarSign,
  Users,
  RefreshCw,
  CreditCard,
  CheckCircle,
  Clock,
  AlertCircle,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "../../utils/helpers";
import {
  getAffiliatePayoutDataV2,
  clearPayoutCache,
} from "../../lib/payoutServiceOptimized";
// Removed useAffiliateId and useAffiliateIdEmail hooks - no longer needed with Wise integration
import { payoutTransactionService } from "../../lib/payoutTransactionService";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";
import DateRangePicker from "../DateRangePicker";
import { useAuth } from "../../context/AuthContext";
import { AffiliateHeaderContext } from "../../context/AffiliateHeaderContext";
import CustomSelect from "../ui/CustomSelect";
import CustomCheckbox from "../ui/CustomCheckbox";
import PaymentRequestModal from "../ui/PaymentRequestModal";
import useAffiliateData from "../../hooks/useAffiliateData";
import SkeletonCard from "../ui/SkeletonCard";
import SkeletonPayoutCard from "../ui/SkeletonPayoutCard";
import WiseRecipientModal from "../affiliate-details/WiseRecipientModal";
import { useAffiliateIdEmail } from "../../hooks/useAffiliateIdemail";
import { getCanonicalAffiliateName } from "../../lib/affiliateIdentity";

// Helper to safely parse numbers
const parseNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : Math.round(n * 100) / 100;
};

// Helper to validate date value
const isValidDate = (d) => d instanceof Date && !isNaN(d);

// Minimum payout requirement
const MINIMUM_PAYOUT = 25;

const PayoutPage = () => {
  const { affiliateName } = useParams();
  const location = useLocation();
  const { user, isAdmin, profile } = useAuth();
  const setHeaderContent = useContext(AffiliateHeaderContext);
  const decodedAffiliateName = decodeURIComponent(affiliateName);
  const canonicalAffiliateName = getCanonicalAffiliateName(profile, user);
  const effectiveAffiliateName = isAdmin
    ? decodedAffiliateName
    : canonicalAffiliateName;

  // Removed affiliateId - no longer needed with Wise integration

  // Use affiliate data hook for getting available dates
  const { getAvailableDates } = useAffiliateData();

  // Determine if user can access this affiliate's data
  const canAccessData = useMemo(() => {
    if (!user) return false;

    // Admin can access any affiliate's data
    if (isAdmin) return true;

    // Regular users can only access their own data
    const userAffiliateName = canonicalAffiliateName;
    return (
      userAffiliateName.toLowerCase() === decodedAffiliateName.toLowerCase()
    );
  }, [user, isAdmin, canonicalAffiliateName, decodedAffiliateName]);

  // Main state
  const [payoutData, setPayoutData] = useState(null);
  const [adPerformanceData, setAdPerformanceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Date filtering state - keep original logic for payout page
  const [dateFilterType, setDateFilterType] = useState("all");
  const [singleDate, setSingleDate] = useState("");
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [availableDates, setAvailableDates] = useState([]);

  // Show/Hide bank account modal
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  // Tab state for Weekly Earnings History section
  const [activeTab, setActiveTab] = useState("earnings");
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [payoutHistoryLoading, setPayoutHistoryLoading] = useState(false);

  // Build date filter object (similar to Dashboard and PerformancePage)
  const buildDateFilter = useCallback(() => {
    return {
      type: dateFilterType,
      singleDate: dateFilterType === "single" ? singleDate : "",
      dateRangeStart: dateFilterType === "range" ? dateRangeStart : "",
      dateRangeEnd: dateFilterType === "range" ? dateRangeEnd : "",
    };
  }, [dateFilterType, singleDate, dateRangeStart, dateRangeEnd]);

  // Load available dates for date picker
  const loadAvailableDates = useCallback(async () => {
    try {
      const dates = await getAvailableDates();
      setAvailableDates(dates);
    } catch (err) {
      console.error("❌ PAYOUT-PAGE: Error loading available dates:", err);
    }
  }, [getAvailableDates]);

  const loadPayoutData = useCallback(async (forceRefresh = false) => {
    if (!decodedAffiliateName) return;

    setIsLoading(true);
    setError(null);

    try {
      console.log(
        `📊 PAYOUT-PAGE: Loading JIT payout data for ${decodedAffiliateName}`
      );

      // Use v2 payout service with TRUE monthly/weekly data from daily table
      const data = await getAffiliatePayoutDataV2(decodedAffiliateName, {
        forceRefresh,
      });
      console.log(`💰 PAYOUT-PAGE: Loaded v2 payout data:`, data);

      // Debug: Check what we actually received
      if (data) {
        console.log(`📊 PAYOUT-PAGE DEBUG:`, {
          weeklyEarnings: data.weeklyEarnings?.length || 0,
          monthlyBreakdown: data.monthlyBreakdown?.length || 0,
          totalPaid: data.totals?.paid || 0,
          available: data.totals?.available || 0,
          lifetime: data.totals?.lifetime || 0,
          firstWeek: data.weeklyEarnings?.[0]?.weekStart || "none",
          lastWeek:
            data.weeklyEarnings?.[data.weeklyEarnings?.length - 1]?.weekStart ||
            "none",
        });

        // Debug: Check monthly breakdown
        if (data.monthlyBreakdown) {
          console.log(
            `📅 PAYOUT-PAGE MONTHS:`,
            data.monthlyBreakdown.map((m) => `${m.month}: $${m.totalAmount}`)
          );
        }
      }

      setPayoutData(data);
      // Set adPerformanceData to empty array since we don't need it anymore
      setAdPerformanceData([]);
    } catch (err) {
      console.error("❌ PAYOUT-PAGE: Error loading payout data:", err);
      setError("Failed to load payout data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [decodedAffiliateName]);

  // Load payout history (all records)
  const loadPayoutHistory = useCallback(async () => {
    if (!decodedAffiliateName) return;

    setPayoutHistoryLoading(true);
    try {
      console.log(
        `📊 PAYOUT-PAGE: Loading payout history for ${decodedAffiliateName}`
      );

      const result =
        await payoutTransactionService.getAffiliatePayoutRequests(
          decodedAffiliateName
        );

      if (result.success) {
        setPayoutHistory(result.data || []);
        console.log(
          `✅ PAYOUT-PAGE: Loaded ${result.data?.length || 0} payout history records`
        );
      } else {
        console.error(
          "❌ PAYOUT-PAGE: Error loading payout history:",
          result.error
        );
        setPayoutHistory([]);
      }
    } catch (error) {
      console.error("❌ PAYOUT-PAGE: Error loading payout history:", error);
      setPayoutHistory([]);
    } finally {
      setPayoutHistoryLoading(false);
    }
  }, [decodedAffiliateName]);

  // Load payout data when filters change
  // OPTIMIZED: Sequential loading to prevent concurrent database queries
  // This avoids cache thrashing on cold start
  useEffect(() => {
    if (!canAccessData) {
      setError("You don't have permission to access this data.");
      setIsLoading(false);
      return;
    }

    const loadDataSequentially = async () => {
      try {
        // STEP 1: Load critical payout data first (heavy query - 7.5s cold cache)
        console.log('🔄 PAYOUT-PAGE: Starting sequential data load...');
        // Force a fresh read on load so a long-lived tab can never pin a stale
        // "Available for Payout" value (the balance changes daily via reconcile).
        await loadPayoutData(true);

        // STEP 2: Load supplementary data after main data succeeds
        // These can run in parallel since payout data has warmed the cache
        console.log('🔄 PAYOUT-PAGE: Loading supplementary data...');
        await Promise.all([
          loadAvailableDates(),
          loadPayoutHistory()
        ]);

        console.log('✅ PAYOUT-PAGE: All data loaded successfully');
      } catch (err) {
        console.error('❌ PAYOUT-PAGE: Error in sequential load:', err);
        // Error is already handled in individual load functions
      }
    };

    loadDataSequentially();
  }, [
    decodedAffiliateName,
    canAccessData,
    loadPayoutData,
    loadAvailableDates,
    loadPayoutHistory,
  ]);

  // Removed aggressive visibility/focus refresh - only refresh on specific payout events
  // This prevents unnecessary refreshes when opening modals or other unrelated actions

  // Refresh data only when navigating from admin pages (where payout status might have changed)
  // This ensures fresh data when coming from admin page after approving payouts
  // OPTIMIZED: Sequential refresh to avoid concurrent queries
  useEffect(() => {
    if (!canAccessData || !location.state?.fromAdmin) return;

    const refreshDataSequentially = async () => {
      console.log(
        "🔄 PAYOUT-PAGE: Navigation from admin detected, refreshing data..."
      );
      clearPayoutCache(decodedAffiliateName);

      // Sequential refresh: payout data first, then history
      await loadPayoutData();
      await loadPayoutHistory();
    };

    refreshDataSequentially();
  }, [
    location.pathname,
    location.state,
    canAccessData,
    decodedAffiliateName,
    loadPayoutData,
    loadPayoutHistory,
  ]);

  // Targeted monitoring for payout status changes only
  // This ensures Available for Payout updates ONLY when payout status changes (failed/declined/paid)
  // Reduced frequency polling since we rely on custom events for immediate updates
  useEffect(() => {
    if (!canAccessData || !decodedAffiliateName) return;

    let intervalId;
    let lastPayoutData = null;

    const checkForPayoutStatusChanges = async () => {
      try {
        // Get current payout transactions to check for status changes
        const currentPayouts = await payoutTransactionService.getPayoutRequests(
          {
            affiliateName: decodedAffiliateName,
            page: 1,
            pageSize: 50, // Get recent payouts
          }
        );

        if (currentPayouts.success && currentPayouts.data) {
          const currentPayoutStatuses = currentPayouts.data.map((p) => ({
            id: p.id,
            status: p.status,
            amount: p.amount,
          }));

          // Compare with last known state
          if (lastPayoutData) {
            const statusChanged = currentPayoutStatuses.some((current) => {
              const previous = lastPayoutData.find((p) => p.id === current.id);
              return previous && previous.status !== current.status;
            });

            if (statusChanged) {
              console.log(
                "🔄 PAYOUT-PAGE: Payout status change detected, refreshing available amount..."
              );
              clearPayoutCache(decodedAffiliateName);
              await loadPayoutData();
              await loadPayoutHistory();
            }
          }

          lastPayoutData = currentPayoutStatuses;
        }

        // The available balance changes daily via the reconcile job even when no
        // payout status changes — refresh it here so a long-open tab stays current
        // instead of pinning a stale "Available for Payout" value.
        const freshData = await getAffiliatePayoutDataV2(decodedAffiliateName, {
          forceRefresh: true,
        });
        setPayoutData((prev) =>
          prev && prev.totals?.available === freshData?.totals?.available
            ? prev
            : freshData
        );
      } catch (error) {
        console.error(
          "❌ PAYOUT-PAGE: Error checking payout status changes:",
          error
        );
      }
    };

    // OPTIMIZED: Delay initial check by 5 seconds to avoid concurrent queries on mount
    // This allows payout data to load first and warm the cache
    console.log('⏳ PAYOUT-PAGE: Scheduling status check in 5 seconds...');
    const initialTimeout = setTimeout(() => {
      console.log('🔍 PAYOUT-PAGE: Running initial status check');
      checkForPayoutStatusChanges();
    }, 5000);

    // Then check every 2 minutes for status changes (reduced frequency)
    // We rely more on custom events for immediate updates
    intervalId = setInterval(checkForPayoutStatusChanges, 120000);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [canAccessData, decodedAffiliateName, loadPayoutData, loadPayoutHistory]);

  // Listen for custom payout status change events
  // This allows immediate refresh when status changes are triggered from admin page
  useEffect(() => {
    const handlePayoutStatusChange = (event) => {
      const {
        affiliateName: eventAffiliateName,
        status,
        amount,
      } = event.detail || {};

      if (eventAffiliateName === decodedAffiliateName) {
        console.log(
          `🔄 PAYOUT-PAGE: Received payout status change event: ${status} for ${formatCurrency(amount)}`
        );
        clearPayoutCache(decodedAffiliateName);
        loadPayoutData();
        loadPayoutHistory();
      }
    };

    // Listen for custom events
    window.addEventListener("payoutStatusChanged", handlePayoutStatusChange);

    return () => {
      window.removeEventListener(
        "payoutStatusChanged",
        handlePayoutStatusChange
      );
    };
  }, [decodedAffiliateName, loadPayoutData, loadPayoutHistory]);

  // Set header content for Payout Center
  useEffect(() => {
    if (setHeaderContent) {
      setHeaderContent(
        <div className="flex items-center space-x-4">
          <div className="w-2 h-8 bg-gradient-to-b from-[#B8460E] to-[#8B3A0E] rounded-full"></div>
          <div>
            <h1 className="text-2xl font-bold text-white">Payout Center</h1>
            <p className="text-gray-400 text-sm">
              Manage and track your payout requests.
            </p>
          </div>
        </div>
      );
    }

    // Cleanup function to reset header when component unmounts
    return () => {
      if (setHeaderContent) {
        setHeaderContent(null);
      }
    };
  }, [setHeaderContent]);

  // Clear cache when needed
  const handleClearCache = () => {
    console.log("🧹 PAYOUT-PAGE: Clearing cache and reloading data...");
    clearPayoutCache(decodedAffiliateName);

    // Clear any browser cache as well
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    console.log("🔄 PAYOUT-PAGE: Cache cleared, forcing fresh data load...");
    loadPayoutData(); // Reload fresh data
    loadPayoutHistory(); // Reload payout history
  };

  // Calculate monthly breakdown from weekly data
  // Monthly breakdown now comes directly from SQL (TRUE calendar months)
  const monthlyBreakdown = useMemo(() => {
    return (payoutData?.monthlyBreakdown || []).sort((a, b) =>
      (b.monthKey || "").localeCompare(a.monthKey || "")
    );
  }, [payoutData?.monthlyBreakdown]);

  // Derive month options from weekly data for the filter dropdown
  const weekMonths = useMemo(() => {
    const months = new Map();
    (payoutData?.weeklyEarnings || []).forEach((week) => {
      if (!week.weekStart) return;
      const date = new Date(week.weekStart);
      if (!isValidDate(date)) return;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const monthName = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
      months.set(monthKey, monthName);
    });
    return Array.from(months.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, name]) => ({ value: key, label: name }));
  }, [payoutData?.weeklyEarnings]);

  // Filter weeks by selected month
  const filteredWeeks = useMemo(() => {
    if (!payoutData?.weeklyEarnings || selectedMonth === "all") {
      return (payoutData?.weeklyEarnings || []).filter(
        (week) => week && week.weekStart && week.weekEnd
      );
    }

    return payoutData.weeklyEarnings.filter((week) => {
      // Check for valid week data first
      if (!week || !week.weekStart || !week.weekEnd) return false;

      const date = new Date(week.weekStart);
      if (!isValidDate(date)) return false;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      return monthKey === selectedMonth;
    });
  }, [payoutData, selectedMonth]);

  // Calculate selected amount
  const selectedAmount = useMemo(() => {
    return selectedWeeks.reduce((sum, weekStart) => {
      const week = filteredWeeks.find((w) => w.weekStart === weekStart);
      return sum + parseNum(week?.amount || 0);
    }, 0);
  }, [selectedWeeks, filteredWeeks]);

  // Filter payout history based on date range picker
  const filteredPayoutHistory = useMemo(() => {
    if (!payoutHistory || payoutHistory.length === 0) return [];

    // If no date filter is active, return all payout history
    if (dateFilterType === "all") {
      return payoutHistory;
    }

    return payoutHistory.filter((payout) => {
      if (!payout.created_at) return false;

      const payoutDate = new Date(payout.created_at);
      if (!isValidDate(payoutDate)) return false;

      if (dateFilterType === "single" && singleDate) {
        const filterDate = new Date(singleDate);
        return payoutDate.toDateString() === filterDate.toDateString();
      }

      if (dateFilterType === "range" && dateRangeStart && dateRangeEnd) {
        const startDate = new Date(dateRangeStart);
        const endDate = new Date(dateRangeEnd);
        return payoutDate >= startDate && payoutDate <= endDate;
      }

      return true;
    });
  }, [payoutHistory, dateFilterType, singleDate, dateRangeStart, dateRangeEnd]);

  const handleWeekToggle = (weekStart) => {
    setSelectedWeeks((prev) =>
      prev.includes(weekStart)
        ? prev.filter((w) => w !== weekStart)
        : [...prev, weekStart]
    );
  };

  const handleSubmitRequest = async (paymentRequestData) => {
    // Check if user is authenticated
    if (!user?.id) {
      setError("User not authenticated. Please log in again.");
      return;
    }

    // Check minimum payout requirement
    if (paymentRequestData.amount < MINIMUM_PAYOUT) {
      setError(`Minimum payout amount is ${formatCurrency(MINIMUM_PAYOUT)}.`);
      return;
    }

    setIsSubmitting(true);
    try {
      console.log(
        `🚀 PAYOUT-PAGE: Submitting payout request for ${decodedAffiliateName}`
      );

      // Format request data for new payout transaction service
      // Use the user's profile username for RLS policy compliance
      const actualAffiliateName = effectiveAffiliateName;
      const requestData = {
        affiliateName: actualAffiliateName,
        selectedWeeks: selectedWeeks.map((weekStart) => {
          const week = filteredWeeks.find((w) => w.weekStart === weekStart);
          return {
            weekStart: week.weekStart,
            weekEnd: week.weekEnd,
            amount: week.amount,
            commission: week.commission,
            referralBonus: week.referralBonus,
            totalSpend: week.totalSpend,
            totalRevenue: week.totalRevenue,
            roas: week.roas,
            adCount: week.adCount,
            purchases: week.purchases,
          };
        }),
        totalAmount: selectedAmount,
        amount: paymentRequestData.amount,
        paymentMethod: paymentRequestData.paymentMethod,
        paymentDetails: paymentRequestData.paymentDetails,
        notes: paymentRequestData.notes,
      };

      // Use new payout transaction service
      const result =
        await payoutTransactionService.submitPayoutRequest(requestData);

      if (result.success) {
        console.log(
          `✅ PAYOUT-PAGE: Payout request submitted successfully for ${decodedAffiliateName}`
        );

        // Reset form and reload data
        setShowPaymentModal(false);
        setSelectedWeeks([]);
        await loadPayoutData();

        // Show success message
        setError(null);
        toast.success(
          "Payout request submitted successfully! You will be notified when it's reviewed by an admin."
        );
      } else {
        throw new Error(result.message || "Failed to submit payout request");
      }
    } catch (error) {
      console.error("❌ PAYOUT-PAGE: Error submitting payout request:", error);
      setError(
        error.message || "Failed to submit payout request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatWeekRange = (weekStart, weekEnd) => {
    // Check for null/undefined values first
    if (!weekStart || !weekEnd) {
      return "Invalid Date Range";
    }

    // Parse dates as local dates to avoid timezone conversion issues
    // Split the date string and create Date objects with local timezone
    const startParts = weekStart.split("-");
    const endParts = weekEnd.split("-");

    // Validate that we have valid date parts
    if (startParts.length !== 3 || endParts.length !== 3) {
      return "Invalid Date Format";
    }

    const start = new Date(
      parseInt(startParts[0]),
      parseInt(startParts[1]) - 1,
      parseInt(startParts[2])
    );
    const end = new Date(
      parseInt(endParts[0]),
      parseInt(endParts[1]) - 1,
      parseInt(endParts[2])
    );

    if (!isValidDate(start) || !isValidDate(end)) return "Invalid Date";

    const formattedStart = start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const formattedEnd = end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    return `${formattedStart} - ${formattedEnd}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "available":
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-400" />;
      case "approved":
        return <CheckCircle className="h-4 w-4 text-blue-400" />;
      case "paid":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "declined":
        return <AlertCircle className="h-4 w-4 text-red-400" />;
      case "failed":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "available":
        return "text-green-400 bg-green-400/10 border-green-400/20";
      case "pending":
        return "text-yellow-400 bg-yellow-400/10 border-yellow-400/20";
      case "approved":
        return "text-blue-400 bg-blue-400/10 border-blue-400/20";
      case "paid":
        return "text-green-500 bg-green-500/10 border-green-500/20";
      case "declined":
        return "text-red-400 bg-red-400/10 border-red-400/20";
      case "failed":
        return "text-red-500 bg-red-500/10 border-red-500/20";
      default:
        return "text-gray-400 bg-gray-400/10 border-gray-400/20";
    }
  };

  const getPayoutHistoryItemTitle = (payout) => {
    const options = { month: "short" };
    if (payout.reviewed_at) {
      const startDate = new Date(payout.reviewed_at);
      const startMonth = startDate.toLocaleString("en-US", options);
      const startDay = startDate.getDate();
      const startFormatted = `${startMonth} ${startDay}`;
      return `Payout on ${startFormatted}`;
    } else if (payout.request_date) {
      const startDate = new Date(payout.request_date);
      const startMonth = startDate.toLocaleString("en-US", options);
      const startDay = startDate.getDate();
      const startFormatted = `${startMonth} ${startDay}`;
      return `Payout on ${startFormatted}`;
    }
    return "Payout";
  };

  if (!canAccessData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Alert
          type="error"
          message="You don't have permission to access this data."
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Page Header Skeleton */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <SkeletonCard width="w-48" height="h-8" className="mb-2" />
            <SkeletonCard width="w-64" height="h-4" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Range Picker Skeleton */}
            <div className="flex items-center gap-2">
              <SkeletonCard width="w-32" height="h-10" rounded="rounded-lg" />
              <SkeletonCard width="w-32" height="h-10" rounded="rounded-lg" />
            </div>
            {/* Refresh Button Skeleton */}
            <SkeletonCard width="w-24" height="h-10" rounded="rounded-lg" />
            {/* Request Payout Button Skeleton */}
            <SkeletonCard width="w-32" height="h-10" rounded="rounded-lg" />
          </div>
        </div>

        {/* Summary Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SkeletonPayoutCard iconBgColor="bg-green-400/10" />
          <SkeletonPayoutCard iconBgColor="bg-purple-400/10" />
          <SkeletonPayoutCard iconBgColor="bg-yellow-400/10" />
          <SkeletonPayoutCard iconBgColor="bg-blue-400/10" />
        </div>

        {/* Monthly Breakdown Skeleton */}
        <div className="bg-white/5 border border-white/10 rounded-xl shadow-lg p-6 backdrop-blur-xl">
          <div className="flex items-center justify-between mb-4">
            <SkeletonCard width="w-48" height="h-6" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="p-4 bg-white/5 rounded-lg border border-white/10"
              >
                <SkeletonCard width="w-32" height="h-5" className="mb-3" />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((j) => (
                    <div key={j} className="flex justify-between">
                      <SkeletonCard width="w-20" height="h-3" />
                      <SkeletonCard width="w-16" height="h-3" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Weekly Earnings History Skeleton */}
        <div className="bg-white/5 border border-white/10 rounded-xl shadow-lg p-6 backdrop-blur-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <SkeletonCard width="w-48" height="h-6" className="mb-2" />
              <SkeletonCard width="w-32" height="h-4" />
            </div>
            <div className="flex items-center space-x-3">
              <SkeletonCard width="w-32" height="h-8" rounded="rounded-lg" />
              <SkeletonCard width="w-32" height="h-8" rounded="rounded-lg" />
            </div>
          </div>

          {/* Instructions Skeleton */}
          <div className="mb-4 space-y-3">
            <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="p-1 bg-white/10 rounded">
                  <SkeletonCard width="w-4" height="h-4" rounded="rounded" />
                </div>
                <SkeletonCard width="w-64" height="h-4" />
              </div>
            </div>
            <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="p-1 bg-white/10 rounded">
                  <SkeletonCard width="w-4" height="h-4" rounded="rounded" />
                </div>
                <SkeletonCard width="w-72" height="h-4" />
              </div>
            </div>
          </div>

          {/* Weekly Earnings List Skeleton */}
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-white/5 rounded-lg border border-white/10"
              >
                <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                  <SkeletonCard width="w-4" height="h-4" rounded="rounded" />
                  <div>
                    <SkeletonCard width="w-48" height="h-5" className="mb-2" />
                    <SkeletonCard width="w-64" height="h-4" />
                  </div>
                </div>
                <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto space-x-3">
                  <div className="text-left sm:text-right">
                    <SkeletonCard width="w-20" height="h-5" className="mb-1" />
                    <SkeletonCard width="w-24" height="h-4" />
                  </div>
                  <div className="flex items-center space-x-1 px-2 py-1 rounded-full border border-white/10">
                    <SkeletonCard width="w-4" height="h-4" rounded="rounded" />
                    <SkeletonCard width="w-16" height="h-3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <Alert type="error" message={error} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Payout Overview</h2>
          <p className="text-gray-400">
            Track your earnings and request payouts for completed work
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Only show Payout button for owners and admins */}
          {/* {(isOwner || isAdmin) && (
            <Button
              variant="secondary"
              onClick={() => {
                setShowPayoutModal(true);
                setPayoutSuccess(false);
              }}
              disabled={isLoading}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20"
            >
              Payout
            </Button>
          )} */}

          {/* Manage Bank Account Button */}
          <div className="relative group">
            <Button
              onClick={() => setShowPayoutModal(true)}
              className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white transition-all duration-200"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Manage Bank Account
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">Available for Payout</p>
              <p
                className={`text-2xl font-bold ${
                  (payoutData?.totals?.available || 0) >= MINIMUM_PAYOUT
                    ? "text-green-400"
                    : "text-yellow-400"
                }`}
              >
                {formatCurrency(parseNum(payoutData?.totals?.available || 0))}
              </p>
              {(payoutData?.totals?.available || 0) < MINIMUM_PAYOUT && (
                <p className="text-xs text-yellow-300 mt-1">
                  Need{" "}
                  {formatCurrency(
                    parseNum(
                      MINIMUM_PAYOUT - (payoutData?.totals?.available || 0)
                    )
                  )}{" "}
                  for minimum payout
                </p>
              )}
            </div>
            <div
              className={`p-3 rounded-full ${
                (payoutData?.totals?.available || 0) >= MINIMUM_PAYOUT
                  ? "bg-green-400/10"
                  : "bg-yellow-400/10"
              }`}
            >
              <DollarSign
                className={`h-6 w-6 ${
                  (payoutData?.totals?.available || 0) >= MINIMUM_PAYOUT
                    ? "text-green-400"
                    : "text-yellow-400"
                }`}
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">Referral Earnings</p>
              <p className="text-2xl font-bold text-purple-400">
                {formatCurrency(
                  parseNum(payoutData?.totals?.unpaidBonuses || 0)
                )}
              </p>
            </div>
            <div className="p-3 bg-purple-400/10 rounded-full">
              <Users className="h-6 w-6 text-purple-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">Pending Approval</p>
              <p className="text-2xl font-bold text-yellow-400">
                {formatCurrency(parseNum(payoutData?.totals?.pending || 0))}
              </p>
            </div>
            <div className="p-3 bg-yellow-400/10 rounded-full">
              <Clock className="h-6 w-6 text-yellow-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">Total Paid</p>
              <p className="text-2xl font-bold text-blue-400">
                {formatCurrency(parseNum(payoutData?.totals?.paid || 0))}
              </p>
            </div>
            <div className="p-3 bg-blue-400/10 rounded-full">
              <CheckCircle className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Monthly Breakdown */}
      {monthlyBreakdown.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Monthly Breakdown{" "}
            <span className="text-sm text-gray-400 font-normal">
              (All time)
            </span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {monthlyBreakdown.map((month) => (
              <div
                key={month.monthKey}
                className="p-4 bg-white/5 rounded-lg border border-white/10"
              >
                <h4 className="font-medium text-white mb-2">{month.month}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Earned:</span>
                    <span className="text-green-400 font-medium">
                      {formatCurrency(parseNum(month.totalAmount))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Ad Spend:</span>
                    <span className="text-gray-300">
                      {formatCurrency(parseNum(month.totalSpend))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Revenue:</span>
                    <span className="text-gray-300">
                      {formatCurrency(parseNum(month.totalRevenue))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Avg ROAS:</span>
                    <span className="text-gray-300">
                      {parseNum(month.roas).toFixed(2)}x
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Ads:</span>
                    <span className="text-gray-300">{month.totalAds}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Earnings & Payout History Tabs */}
      <Card className="p-6 relative">
        {/* Tab Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex space-x-1 bg-white/5 rounded-lg p-1">
            <button
              onClick={() => setActiveTab("earnings")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === "earnings"
                  ? "bg-[#B8460E] text-white shadow-lg"
                  : "text-gray-400 hover:text-white hover:bg-white/10"
              }`}
            >
              Weekly Earnings History
              <span className="ml-2 text-xs opacity-75">
                ({filteredWeeks.length})
              </span>
            </button>
            <button
              onClick={() => setActiveTab("payouts")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === "payouts"
                  ? "bg-[#B8460E] text-white shadow-lg"
                  : "text-gray-400 hover:text-white hover:bg-white/10"
              }`}
            >
              Payout History
              <span className="ml-2 text-xs opacity-75">
                ({filteredPayoutHistory.length})
              </span>
            </button>
          </div>

          {/* Tab-specific controls */}
          {activeTab === "earnings" && (
            <div className="flex items-center space-x-3">
              <label className="hidden sm:block text-sm text-gray-400 whitespace-nowrap">
                Filter by Month:
              </label>
              <CustomSelect
                value={selectedMonth}
                onChange={setSelectedMonth}
                options={[
                  { value: "all", label: "All Months" },
                  ...weekMonths,
                ]}
              />
              <Button
                onClick={handleClearCache}
                className="bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>
          )}

          {activeTab === "payouts" && (
            <div className="flex items-center space-x-3">
              <Button
                onClick={loadPayoutHistory}
                disabled={payoutHistoryLoading}
                className="bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-2"
              >
                {payoutHistoryLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === "earnings" && (
          <div>
            {/* Tab Title and Description */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-white mb-2">
                Weekly Earnings History
              </h4>
            </div>

            {filteredWeeks.length === 0 ? (
              <div className="text-center py-8">
                <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-400 mb-2">
                  No Earnings Data
                </h4>
                <p className="text-gray-500">
                  Earnings will appear here once ad performance data is
                  processed.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredWeeks
                  .filter((week) => week && week.weekStart && week.weekEnd)
                  .map((week) => (
                    <div
                      key={week.weekStart}
                      className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-lg border transition-all duration-200 ${
                        selectedWeeks.includes(week.weekStart)
                          ? "bg-[#B8460E]/20 border-[#B8460E]/40 shadow-lg shadow-[#B8460E]/10"
                          : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                        <div>
                          <div className="font-medium text-white">
                            Week of{" "}
                            {formatWeekRange(week.weekStart, week.weekEnd)}
                            {selectedWeeks.includes(week.weekStart) && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-[#B8460E]/30 text-[#B8460E] border border-[#B8460E]/40">
                                Selected
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-400">
                            {week.adCount} ads •{" "}
                            {formatCurrency(parseNum(week.totalSpend))} spent •{" "}
                            {week.avgROAS?.toFixed(2)} Avg ROAS
                            {week.referralBonus > 0 && (
                              <span className="text-purple-400">
                                {" "}
                                • +
                                {formatCurrency(
                                  parseNum(week.referralBonus)
                                )}{" "}
                                referral
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto space-x-3">
                        <div className="text-left sm:text-right">
                          <div
                            className={`font-semibold transition-colors ${
                              selectedWeeks.includes(week.weekStart)
                                ? "text-[#B8460E]"
                                : "text-white"
                            }`}
                          >
                            {formatCurrency(parseNum(week.amount))}
                          </div>
                          {week.referralBonus > 0 && (
                            <div className="text-xs text-purple-400">
                              (includes{" "}
                              {formatCurrency(parseNum(week.referralBonus))}{" "}
                              referral)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "payouts" && (
          <div>
            {/* Tab Title and Description */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-white mb-2">
                Payout History
              </h4>
            </div>

            {payoutHistoryLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-4 animate-spin" />
                <h4 className="text-lg font-medium text-gray-400 mb-2">
                  Loading Payout History...
                </h4>
              </div>
            ) : filteredPayoutHistory.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-400 mb-2">
                  No Payout History
                </h4>
                <p className="text-gray-500">
                  {dateFilterType !== "all"
                    ? "No payout requests found for the selected date range."
                    : "Your payout requests will appear here once you submit them."}
                </p>
              </div>
            ) : (
              <div>
                <div className="space-y-3 mb-6">
                  {filteredPayoutHistory.map((payout) => (
                    <div
                      key={payout.id}
                      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-lg border bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-200"
                    >
                      <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                        <div>
                          <div className="font-medium text-white">
                            {getPayoutHistoryItemTitle(payout)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto space-x-3">
                        <div className="text-left sm:text-right">
                          <div className="font-semibold text-white">
                            {formatCurrency(parseNum(payout.amount))}
                          </div>
                          {payout.reviewed_at && (
                            <div className="text-xs text-gray-400">
                              Reviewed{" "}
                              {new Date(payout.reviewed_at).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                }
                              )}
                            </div>
                          )}
                        </div>
                        <div
                          className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            payout.status
                          )}`}
                        >
                          {getStatusIcon(payout.status)}
                          <span className="capitalize">{payout.status}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* New Multi-Step Payment Request Modal */}
      <PaymentRequestModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSubmit={handleSubmitRequest}
        amount={selectedAmount}
        currency="USD"
        affiliateName={effectiveAffiliateName}
      />

      <WiseRecipientModal
        isOpen={showPayoutModal}
        onClose={() => setShowPayoutModal(false)}
        affiliateName={effectiveAffiliateName}
        onSaved={() => setShowPayoutModal(false)}
      />
    </div>
  );
};

export default PayoutPage;
